import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { api, apiError } from "@/lib/api";
import { hoyChicago } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileText, Download, Mail, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

const TIPOS = [
  { value: "diesel", get label() { return t("Diésel"); } },
  { value: "cargas", get label() { return t("Cargas"); } },
  { value: "jornadas", get label() { return t("Jornadas"); } },
  { value: "incidentes", get label() { return t("Incidentes"); } },
  { value: "insumos", get label() { return t("Insumos"); } },
];

function rangoSemana() {
  const hoy = dayjs(hoyChicago());
  const inicio = hoy.subtract((hoy.day() + 6) % 7, "day");
  return { desde: inicio.format("YYYY-MM-DD"), hasta: inicio.add(6, "day").format("YYYY-MM-DD") };
}
function rangoMes() {
  const hoy = dayjs(hoyChicago());
  return { desde: hoy.startOf("month").format("YYYY-MM-DD"), hasta: hoy.endOf("month").format("YYYY-MM-DD") };
}
function rangoTrimestre(offset = 0) {
  const hoy = dayjs(hoyChicago());
  let qIndex = Math.floor(hoy.month() / 3) - offset;
  let year = hoy.year();
  while (qIndex < 0) { qIndex += 4; year -= 1; }
  const inicio = dayjs(`${year}-${String(qIndex * 3 + 1).padStart(2, "0")}-01`);
  return { desde: inicio.format("YYYY-MM-DD"), hasta: inicio.add(2, "month").endOf("month").format("YYYY-MM-DD") };
}

export default function Reportes() {
  const [tipo, setTipo] = useState("diesel");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [incluirFotos, setIncluirFotos] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [reportes, setReportes] = useState([]);
  const [emailsSugeridos, setEmailsSugeridos] = useState([]);

  const cargarReportes = useCallback(async () => {
    try { const { data } = await api.get("/reportes"); setReportes(data); } catch { /* */ }
  }, []);

  useEffect(() => {
    cargarReportes();
    api.get("/reportes/emails-sugeridos").then((r) => setEmailsSugeridos(r.data.emails || [])).catch(() => {});
  }, [cargarReportes]);

  const aplicarAtajo = (fn) => { const r = fn(); setDesde(r.desde); setHasta(r.hasta); };

  const generar = async () => {
    if (!desde || !hasta) { toast.error(t("Elige el rango de fechas")); return; }
    setGenerando(true);
    try {
      await api.post("/reportes/generar", { tipo, desde, hasta, incluir_fotos: tipo === "diesel" ? incluirFotos : false });
      toast.success(t("Reporte generado"));
      cargarReportes();
    } catch (e) { toast.error(apiError(e)); } finally { setGenerando(false); }
  };

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <div>
        <h1 className="font-display font-bold text-[26px] lg:text-[30px] leading-[1.05] text-ink" data-testid="page-title">{t("Reportes")}</h1>
        <p className="text-sm text-ink-3">{t("Genera reportes en PDF para descargar o enviar por correo")}</p>
      </div>

      <div className="bg-white rounded-xl border p-6 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="eyebrow">{t("Tipo")}</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="reportes-tipo-select" className="h-10 w-44 bg-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {TIPOS.map((tt) => <SelectItem key={tt.value} value={tt.value}>{tt.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="eyebrow">{t("Desde")}</Label>
            <Input data-testid="reportes-desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-10 w-40 bg-white mt-1" />
          </div>
          <div>
            <Label className="eyebrow">{t("Hasta")}</Label>
            <Input data-testid="reportes-hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-10 w-40 bg-white mt-1" />
          </div>
          {tipo === "diesel" && (
            <label className="flex items-center gap-2 text-sm text-ink-2 h-10">
              <input data-testid="reportes-incluir-fotos" type="checkbox" checked={incluirFotos} onChange={(e) => setIncluirFotos(e.target.checked)} className="h-4 w-4" />{t(" Incluir fotos")}</label>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="reportes-atajo-semana" variant="outline" size="sm" className="h-9 rounded-full" onClick={() => aplicarAtajo(rangoSemana)}>{t("Esta semana")}</Button>
          <Button data-testid="reportes-atajo-mes" variant="outline" size="sm" className="h-9 rounded-full" onClick={() => aplicarAtajo(rangoMes)}>{t("Este mes")}</Button>
          <Button data-testid="reportes-atajo-trimestre" variant="outline" size="sm" className="h-9 rounded-full" onClick={() => aplicarAtajo(() => rangoTrimestre(0))}>{t("Este trimestre")}</Button>
          <Button data-testid="reportes-atajo-trimestre-anterior" variant="outline" size="sm" className="h-9 rounded-full" onClick={() => aplicarAtajo(() => rangoTrimestre(1))}>{t("Trimestre anterior")}</Button>
        </div>
        <Button data-testid="reportes-generar-btn" onClick={generar} disabled={generando} className="h-11 px-8">
          {generando ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Generar PDF")}
        </Button>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-6 py-4 border-b"><p className="eyebrow">{t("Reportes generados")}</p></div>
        <div className="divide-y">
          {reportes.length === 0 && <p className="px-6 py-10 text-center text-ink-3 text-sm">{t("Todavía no generaste ningún reporte")}</p>}
          {reportes.map((r) => <ReporteFila key={r.id} reporte={r} emailsSugeridos={emailsSugeridos} />)}
        </div>
      </div>
    </div>
  );
}

function ReporteFila({ reporte, emailsSugeridos }) {
  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [mostrarEmail, setMostrarEmail] = useState(false);

  const descargar = async () => {
    try {
      const res = await api.get(`/reportes/${reporte.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = t("reporte_{tipo}_{desde}_{hasta}.pdf", { tipo: reporte.tipo, desde: reporte.desde, hasta: reporte.hasta }); a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const enviar = async () => {
    if (!email.trim()) { toast.error(t("Ingresa un correo")); return; }
    setEnviando(true);
    try {
      const { data } = await api.post(`/reportes/${reporte.id}/enviar`, { email: email.trim() });
      if (data.enviado) { toast.success(t("Enviado a {v0}", { v0: email.trim() })); setMostrarEmail(false); setEmail(""); }
      else toast.error(data.motivo || t("Correo no configurado"));
    } catch (e) { toast.error(apiError(e)); } finally { setEnviando(false); }
  };

  // Prompt L (2026-10-01): un clic manda el reporte a todos los destinatarios de reportes.
  const enviarDestinatarios = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post(`/reportes/${reporte.id}/enviar-destinatarios`);
      if (data.enviados.length) toast.success(t("Enviado a {v0}", { v0: data.enviados.join(", ") }));
      data.errores.forEach((e) => toast.error(`${e.email}: ${e.detalle}`));
    } catch (e) { toast.error(apiError(e)); } finally { setEnviando(false); }
  };

  return (
    <div data-testid={`reporte-fila-${reporte.id}`} className="px-6 py-4 flex items-center justify-between flex-wrap gap-3">
      <div>
        <p className="font-semibold text-ink">{t("{tipo} — {desde} a {hasta}", { tipo: t(TIPOS.find((x) => x.value === reporte.tipo)?.label || reporte.tipo), desde: reporte.desde, hasta: reporte.hasta })}</p>
        <p className="text-xs text-ink-3">{t("Generado por {creado_por}{v1}", { creado_por: reporte.creado_por, v1: reporte.enviado_a?.length > 0 ? t(" · Enviado a {v0}", { v0: reporte.enviado_a.map((e) => e.email).join(", ") }) : "" })}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid={`reporte-descargar-${reporte.id}`} variant="outline" size="sm" onClick={descargar}><Download className="h-4 w-4 mr-1" />{t(" Descargar")}</Button>
        {!mostrarEmail ? (
          <>
            <Button data-testid={`reporte-enviar-destinatarios-${reporte.id}`} variant="outline" size="sm" onClick={enviarDestinatarios} disabled={enviando}>{enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Users className="h-4 w-4 mr-1" />{t(" A destinatarios")}</>}</Button>
            <Button data-testid={`reporte-mostrar-email-${reporte.id}`} variant="outline" size="sm" onClick={() => setMostrarEmail(true)}><Mail className="h-4 w-4 mr-1" />{t(" Otro correo")}</Button>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <Input data-testid={`reporte-email-input-${reporte.id}`} list={`emails-sugeridos-${reporte.id}`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("correo@empresa.com")} className="h-9 w-56" />
            <datalist id={`emails-sugeridos-${reporte.id}`}>{emailsSugeridos.map((e) => <option key={e} value={e} />)}</datalist>
            <Button data-testid={`reporte-enviar-email-${reporte.id}`} size="sm" className="bg-brand hover:bg-brand-dark text-white" onClick={enviar} disabled={enviando}>{enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Enviar")}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
